import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import useActivityInput, {
  ActivityInputValues,
  defaultActivityInputValues,
} from 'components/shared/activityInput/useActivityInput';
import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useTranslation } from 'context/translations';
import { simpleDate } from 'lib/date';
import { ActivityType } from 'models/Activity';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionType } from 'models/QuestionTemplate';
import { activityQueryKeys, createActivity, ProgressUpdatePayload } from 'services/api/activity';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { episodeQueryKeys } from 'services/api/episode';
import { progressChartQueryKeys } from 'services/api/locationEpisode/progressChart';
import { createLocationEpisodeAdmission } from 'services/api/locationEpisodeAdmissions';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { useToastActions } from 'stores/toastStore';

import QuestionList, { AnswerType, QuestionListRefType } from './QuestionList/QuestionList';

export type DischargeModalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  locationEpisode: LocationEpisode;
  episode: Episode;
  title: string;
  invalidateData?: () => void;
  linkedRecords?: LocationEpisode[];
};

type UniqueTemplate = {
  templateId: string;
  questions: QuestionType[];
  locationEpisodeIds: string[];
  displayNames: string[];
};

function groupByTemplate(linkedRecords: LocationEpisode[], currentLocationEpisode: LocationEpisode): UniqueTemplate[] {
  const templateMap = new Map<string, UniqueTemplate>();

  // Add current location episode first (use contentKey to group by content, not ID)
  const currentContentKey = currentLocationEpisode.progressTemplate.contentKey;
  templateMap.set(currentContentKey, {
    templateId: currentContentKey,
    questions: currentLocationEpisode.progressTemplate.questions,
    locationEpisodeIds: [currentLocationEpisode.id],
    displayNames: [currentLocationEpisode.owner.client?.name || currentLocationEpisode.group.name],
  });

  // Add linked records
  linkedRecords.forEach((link) => {
    if (link.id === currentLocationEpisode.id) return;

    const contentKey = link.progressTemplate.contentKey;
    const existing = templateMap.get(contentKey);

    if (existing) {
      existing.locationEpisodeIds.push(link.id);
      existing.displayNames.push(link.owner.client?.name || link.group.name);
    } else {
      templateMap.set(contentKey, {
        templateId: contentKey,
        questions: link.progressTemplate.questions,
        locationEpisodeIds: [link.id],
        displayNames: [link.owner.client?.name || link.group.name],
      });
    }
  });

  return Array.from(templateMap.values());
}

export default function PatientStatusUpdateModal(props: DischargeModalType) {
  const { setShow, patientName, locationEpisode, title, invalidateData, episode, linkedRecords = [] } = props;
  const questionsRef = useRef<QuestionListRefType>(null);
  const [admittedDate, setAdmittedDate] = useState<Date | null>(null);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { note, setNote, resetNote, getSerializedNoteValues } = useActivityInput();
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  // Multi-step wizard state
  const isSharedFlow = linkedRecords.length > 0;
  const uniqueTemplates = isSharedFlow ? groupByTemplate(linkedRecords, locationEpisode) : [];
  const totalSteps = uniqueTemplates.length;
  const isSingleTemplate = totalSteps <= 1;

  const [currentStep, setCurrentStep] = useState(0);
  const [collectedAnswers, setCollectedAnswers] = useState<Map<string, AnswerType[]>>(new Map());
  const [collectedNotes, setCollectedNotes] = useState<Map<string, ActivityInputValues>>(new Map());

  const currentTemplate = isSharedFlow ? uniqueTemplates[currentStep] : null;
  const isLastStep = !isSharedFlow || currentStep === totalSteps - 1;

  const currentStepLocationEpisode = useMemo(() => {
    if (!currentTemplate) return locationEpisode;
    const leId = currentTemplate.locationEpisodeIds[0];
    return [locationEpisode, ...linkedRecords].find((le) => le.id === leId) ?? locationEpisode;
  }, [currentTemplate, locationEpisode, linkedRecords]);

  const isAdmitting =
    locationEpisode.currentRehabState.queue || (isSharedFlow && linkedRecords.some((lr) => lr.inQueue));

  const { mutate: mutateActivity, isPending: isActivityPending } = useMutation({
    mutationFn: createActivity,
  });

  const { mutate: mutateAdmission, isPending: isAdmissionPending } = useMutation({
    mutationFn: createLocationEpisodeAdmission,
  });

  const isPending = isActivityPending || isAdmissionPending;

  const cancel = () => {
    questionsRef.current?.reset();
    resetNote();
    setAllQuestionsAnswered(false);
    setAdmittedDate(null);
    setCurrentStep(0);
    setCollectedAnswers(new Map());
    setCollectedNotes(new Map());
    setShow(false);
  };

  const saveCurrentStepData = () => {
    if (!currentTemplate) return;
    const answers = questionsRef.current?.getAnswers() ?? [];
    setCollectedAnswers((prev) => {
      const next = new Map(prev);
      next.set(currentTemplate.templateId, answers);
      return next;
    });
    if (!isSingleTemplate) {
      setCollectedNotes((prev) => {
        const next = new Map(prev);
        next.set(currentTemplate.templateId, { ...note });
        return next;
      });
    }
  };

  const restoreStepNotes = (stepIndex: number) => {
    if (isSingleTemplate) return;
    const template = uniqueTemplates[stepIndex];
    const savedNote = collectedNotes.get(template.templateId);
    if (savedNote) {
      setNote(savedNote);
    } else {
      resetNote();
    }
  };

  const handleNext = () => {
    saveCurrentStepData();
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setAllQuestionsAnswered(false);
    restoreStepNotes(nextStep);
  };

  const handleBack = () => {
    saveCurrentStepData();
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    restoreStepNotes(prevStep);
  };

  const handleSuccess = () => {
    // only invalidate episode and location episode if the progress update results in the start of treatment
    if (!locationEpisode.currentRehabState.inTreatment) {
      queryClient.invalidateQueries({ queryKey: episodeQueryKeys.show({ id: locationEpisode.episodeId }) });
    }
    queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show({ id: locationEpisode.id }) });
    queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
    queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.count() });
    queryClient.invalidateQueries({ queryKey: progressChartQueryKeys.show(locationEpisode.id) });
    invalidateData?.();
    cancel();
  };

  const submit = () => {
    if (isSharedFlow) {
      saveCurrentStepData();

      const progressUpdates: ProgressUpdatePayload[] = [];
      uniqueTemplates.forEach((template) => {
        const answers =
          template.templateId === currentTemplate?.templateId
            ? (questionsRef.current?.getAnswers() ?? [])
            : (collectedAnswers.get(template.templateId) ?? []);

        // For multi-template flows, use per-step notes; for single template, use current note
        const templateNote = isSingleTemplate
          ? note
          : template.templateId === currentTemplate?.templateId
            ? note
            : (collectedNotes.get(template.templateId) ?? defaultActivityInputValues);

        template.locationEpisodeIds.forEach((leId) => {
          progressUpdates.push({
            locationEpisodeId: leId,
            questions: answers,
            text: templateNote.text || undefined,
            plaintext: templateNote.plaintext || undefined,
            mentions: templateNote.mentions?.length
              ? templateNote.mentions.map((m) => ({
                  id: m.id,
                  display: m.display,
                  childIndex: m.childIndex,
                  index: m.index,
                  plainTextIndex: m.plainTextIndex,
                }))
              : undefined,
          });
        });
      });

      const mutationCallbacks = {
        onSuccess: handleSuccess,
        onError: () => addToast({ text: 'Something went wrong. Please try again.' }),
      };

      if (isAdmitting) {
        const enteredAt = admittedDate ?? undefined;

        mutateAdmission({ locationEpisodeId: locationEpisode.id, enteredAt, progressUpdates }, mutationCallbacks);
      } else {
        mutateActivity(
          { type: ActivityType.PROGRESS_UPDATE, progressUpdates, locationEpisodeId: locationEpisode.id },
          mutationCallbacks
        );
      }
    } else {
      // TODO: OLIO-2027 - Remove this branch once all admissions use the admission endpoint.
      // This is the legacy path for non-shared-records users where the activity endpoint
      // handles both admission (as a side effect) and progress update creation.
      mutateActivity(
        {
          ...getSerializedNoteValues(),
          type: ActivityType.PROGRESS_UPDATE,
          questions: questionsRef.current!.getAnswers(),
          enteredAt: admittedDate ?? undefined,
          locationEpisodeId: locationEpisode.id,
        },
        {
          onSuccess: handleSuccess,
          onError: () => addToast({ text: 'Something went wrong. Please try again.' }),
        }
      );
    }
  };

  const latestStatus = locationEpisode.latestProgressUpdate?.data?.questions?.find((q) => q.kind === 'status')?.answer
    .text;

  const submitDisabled =
    isUploading || !allQuestionsAnswered || (locationEpisode?.currentRehabState.queue && !admittedDate);

  const questions: QuestionType[] = useMemo(() => {
    const baseQuestions =
      isSharedFlow && currentTemplate ? currentTemplate.questions : locationEpisode.progressTemplate.questions;

    if (!isSharedFlow || !currentTemplate) return baseQuestions;

    const savedAnswers = collectedAnswers.get(currentTemplate.templateId);
    if (!savedAnswers?.length) return baseQuestions;

    return savedAnswers;
  }, [isSharedFlow, currentTemplate, locationEpisode.progressTemplate.questions, collectedAnswers]);

  const subtitle = useMemo(() => {
    if (!isSharedFlow || isSingleTemplate || !currentTemplate) return undefined;
    // Only show sublabel when current step maps to a single LE (not grouped)
    if (currentTemplate.locationEpisodeIds.length > 1) return undefined;
    return `Record ${currentStep + 1} of ${totalSteps}: ${currentTemplate.displayNames[0]}`;
  }, [isSharedFlow, isSingleTemplate, currentTemplate, currentStep, totalSteps]);

  return (
    <StandardModal title={title} subtitle={subtitle} onCancel={cancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormContainer>
        {locationEpisode?.dischargeOverdue && (
          <Alert style={{ marginBottom: 16 }}>
            <Alert.Text>
              Patient&apos;s projected {translate('discharge').toLowerCase()} date of&nbsp;
              <b>{simpleDate(locationEpisode.projectedDischargeReview?.date)}</b>
              &nbsp; has passed. Please provide details regarding any barriers to discharge.
            </Alert.Text>
          </Alert>
        )}
        {locationEpisode?.currentRehabState.queue && (!isSharedFlow || currentStep === 0) && (
          <InputGroup title={`Actual ${translate('Admission')} Date`}>
            <DatePicker
              data-cy='actualAdmissionDate'
              placeholder='Select Date'
              maxDate={new Date()}
              selected={admittedDate}
              onChange={setAdmittedDate}
            />
          </InputGroup>
        )}
        <QuestionList
          ref={questionsRef}
          questions={questions}
          setAllQuestionsAnswered={setAllQuestionsAnswered}
          latestStatus={latestStatus}
          locationEpisode={currentStepLocationEpisode}
          episode={episode}
          noteValues={note}
          setNoteValues={setNote}
          resetNoteValues={resetNote}
          setIsUploading={setIsUploading}
        />
      </FormContainer>
      <Actions>
        {isSharedFlow && currentStep > 0 ? (
          <Button variant='ghost' onClick={handleBack}>
            Back
          </Button>
        ) : (
          <Button variant='ghost' onClick={cancel}>
            Cancel
          </Button>
        )}
        {isLastStep ? (
          <Button onClick={submit} disabled={submitDisabled} loading={isPending}>
            {isSharedFlow && !isSingleTemplate ? 'Submit All' : 'Submit'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={!allQuestionsAnswered}>
            Next
          </Button>
        )}
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  padding-bottom: 20px;
`;
